import { useState, useRef, useEffect, useId } from 'react'
import ControlCheckbox from '@components/common/ControlCheckbox'
import { ChevronDownIcon } from './queue/queueIcons'

/**
 * GL category multi-select for the Category variants of P&L / Balance Sheet
 * (CC-49973). "One / many / all" over the tenant's category list, which V1
 * renders as a plain multi-select in its Accounting Reports Filter dialog
 * (AccountingReportsFilterDialog → ViewBag.CategoryList).
 *
 * Deliberately an INLINE disclosure panel rather than a fixed-position popover:
 * the list is small (a firm's GL categories, not an 800-name client list), and
 * an inline panel has no viewport-clamping or scroll-detachment behaviour to get
 * wrong on a financial page. Keyboard: the toggle is a real <button> with
 * aria-expanded, every option is a real checkbox in a labelled <fieldset>, and
 * Escape closes — so it is operable without a mouse (WCAG 2.1.1).
 *
 * `value` is an array of category int-ids. Empty means "nothing selected", which
 * callers must treat as an invalid category run — NOT as "all" (see
 * toFilteredCategories in useGlCategories.js).
 */
export default function CategorySelect({ categories, value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  // Per-instance id: two pickers on one page (P&L already renders one, and the
  // Balance Sheet another) must not collide on a hardcoded DOM id, or the label
  // points at the wrong control.
  const btnId = useId()

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const list = categories || []
  const picked = Array.isArray(value) ? value : []
  const allIds = list.map((c) => c.objectIntId)
  const isAll = list.length > 0 && picked.length === list.length

  const toggle = (id) =>
    onChange(picked.includes(id) ? picked.filter((p) => p !== id) : [...picked, id])

  // Summary text doubles as the button's accessible name, so a screen-reader
  // user hears the current selection without opening the panel.
  const summary = picked.length === 0
    ? 'None selected'
    : isAll
      ? `All categories (${list.length})`
      : picked.length === 1
        ? (list.find((c) => c.objectIntId === picked[0])?.name || '1 selected')
        : `${picked.length} of ${list.length} selected`

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1" htmlFor={btnId}>
        Categories
      </label>
      <button
        id={btnId}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        // The panel is a group of checkboxes, not a menu — aria-haspopup="true"
        // announces menu semantics a keyboard user then can't act on.
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Categories: ${summary}`}
        // 2026-08-26 ADA sweep. Both states are the ONLY affordance drawing this
        // as a control, so both owe SC 1.4.11 its 3.0:1 — and neither was paying:
        // border-gray-200 measured 1.24:1 and border-amber-300 1.44:1. The normal
        // state takes --color-rc-control (3.03:1). The warning state takes
        // --color-rc-stale-line (3.04:1), which is the token built for exactly
        // this — the lightest tint of its own amber label colour that clears the
        // floor. Its fill is already --color-rc-stale-bg's hex, and text-amber-900
        // on it is 9.2:1, so only the border moved.
        //
        // NOT `.btn-rc`, deliberately: that class is unlayered, so it would have
        // beaten the amber fill, border and text outright and erased the "None
        // selected" warning — the state that stops an empty selection being read
        // as a firm-wide statement (CC-50039).
        className={`flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-1.5 text-left text-sm ${
          picked.length === 0
            ? 'border-rc-stale-line bg-amber-50 text-amber-900'
            : 'border-rc-control bg-white text-gray-700 hover:bg-gray-50'
        } disabled:cursor-not-allowed disabled:opacity-60`}
      >
        <span className="truncate">{summary}</span>
        {/* B6 (spec 117): outline chevron, was a filled ▾. It inherits the
            button's own colour rather than the `text-gray-500` the triangle
            carried — in the "None selected" warning state that grey sat on
            bg-amber-50 at 4.66:1 (recomputed 2026-09-09 from #6b7280 on
            #fffbeb; this note said 4.4:1, which was never right) while
            everything else in the control was
            text-amber-900, so the caret was the one element that did not belong
            to its own state. currentColor makes it follow both states. */}
        <ChevronDownIcon className="h-3.5 w-3.5 flex-none" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-56 rounded-lg border border-gray-200 bg-white p-1 shadow-lg">
          <div className="flex items-center justify-between gap-2 px-2 py-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              {picked.length} of {list.length}
            </span>
            <span className="flex gap-2">
              <button
                type="button"
                onClick={() => onChange(allIds)}
                className="text-[10px] text-cb-accent-text hover:underline"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-[10px] text-cb-accent-text hover:underline"
              >
                Clear
              </button>
            </span>
          </div>
          <fieldset className="max-h-56 overflow-auto">
            <legend className="sr-only">GL categories to include</legend>
            {list.length === 0 ? (
              <div className="px-2 py-1.5 text-xs text-gray-500">No categories defined</div>
            ) : (
              list.map((c) => (
                <label
                  key={c.objectIntId}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                >
                  {/* B4 (spec 117). The note that used to sit here said this
                      carried border-rc-control (3.03:1) rather than
                      border-gray-300 (1.47:1) — TRUE of the class list and FALSE
                      on screen: Tailwind v4 preflight sets `border-width: 0` on
                      `*`, there was no bare `border` width utility beside the
                      colour, and so NEITHER hex was ever painted. The browser
                      drew its default control and the token was decoration in the
                      source only.

                      The drawn control renders --color-rc-seam #6b7280: 4.834 on
                      white, 4.626 on the row's own hover band #f9fafb, 4.393 on
                      #f3f4f6. NOT 3.026 — this line said that until 2026-09-09,
                      which was rc-control's on-WHITE figure and is exactly the
                      number that got the first pass shipped past a review. Do
                      not reinstate rc-control on a drawn box here; it is 2.896
                      on the hover band. See ControlCheckbox.jsx. */}
                  <ControlCheckbox
                    checked={picked.includes(c.objectIntId)}
                    onChange={() => toggle(c.objectIntId)}
                  />
                  <span className="truncate">{c.name}</span>
                </label>
              ))
            )}
          </fieldset>
        </div>
      )}
    </div>
  )
}
